/**
 * Financeiro › Fichas Financeiras da Obra (HU 2679) — contrato de
 * `GET /api/financeiro/obras/{obraId}/fichas-financeiras[/{fichaId}]` (privilégio
 * `hub.financeiro:visualizar-fmm`, verificado pelo core) e, na HU 2684, prévia e criação da ficha
 * (`GET .../previa` e `POST`, privilégio `hub.financeiro:gerar-ficha`).
 */

export interface FichaFinanceiraItem {
  id: string;
  obraId: string;
  /** ISO 8601. */
  criadoEm: string;
  statusFel: string | null;
  /** Nome do solicitante. */
  solicitante: string;
  solicitanteEmail?: string | null;
}

export interface FichasFinanceirasPage {
  items: FichaFinanceiraItem[];
  total: number;
}

/** Campos ordenáveis da listagem (a coluna da lupa não ordena). */
export const FICHAS_FINANCEIRAS_SORT_FIELDS = ["criadoEm", "statusFel", "solicitante"] as const;

export type FichasFinanceirasSortField = (typeof FICHAS_FINANCEIRAS_SORT_FIELDS)[number];

export function isFichasFinanceirasSortField(value: unknown): value is FichasFinanceirasSortField {
  return (FICHAS_FINANCEIRAS_SORT_FIELDS as readonly unknown[]).includes(value);
}

export interface FichasFinanceirasSort {
  field: FichasFinanceirasSortField;
  direction: "asc" | "desc";
}

/** Sem ordem escolhida no cabeçalho: a mais recente primeiro. */
export const DEFAULT_FICHAS_FINANCEIRAS_SORT: FichasFinanceirasSort = {
  field: "criadoEm",
  direction: "desc",
};

/** Máximo aceito pelo bff por página. */
export const MAX_FICHAS_FINANCEIRAS_PAGE_SIZE = 100;

export interface ListFichasFinanceirasParams {
  page?: number;
  pageSize?: number;
  sort?: FichasFinanceirasSort;
  signal?: AbortSignal;
}

/** Dados da obra lidos do banco (Identificação da Obra e Datas e Planejamento). */
export interface FichaObraResumo {
  /** ID da Obra. */
  codigo: string;
  /** Nome da embarcação. */
  embarcacao: string;
  /** Ex.: "Seco (Dry Docking)"; `null` sem inspeção de casco. */
  condicao: string | null;
  /** Local planejado; `null` quando não há. */
  localEstimado: string | null;
  /** Empresa do grupo (grupo da embarcação); `null` quando não informada. */
  empresaGrupo: string | null;
  /** `yyyy-MM-dd`. */
  inicio: string;
  /** `yyyy-MM-dd`. */
  fim: string;
  /** Contagem inclusiva em dias. */
  duracaoDias: number;
  etapas: {
    obraDias: number | null;
    testesDias: number | null;
    aceitacaoDias: number | null;
  };
}

/** Informações da Embarcação lidas do banco; `null` = sem dado. */
export interface FichaInformacoesEmbarcacao {
  bandeira: string | null;
  imo: string | null;
  deadweight: number | null;
  arqueacaoBruta: number | null;
  arqueacaoLiquida: number | null;
  loa: number | null;
  bocaMoldada: number | null;
  caladoMaximo: number | null;
  caladoPrevistoEntrada: number | null;
  deslocamentoPrevistoEntrada: number | null;
  convesComprimento: number | null;
  convesLargura: number | null;
  convesAreaTotal: number | null;
  convesResistencia: number | null;
  convesCapacidade: number | null;
}

/** Escopo macro de um tipo de obra (Custos e Financeiro). */
export interface FichaCustosEscopoMacro {
  id: string;
  nome: string;
}

/** Tipo de obra da tabela "Escopo Macro da Obra"; o dominante recebe a tag. */
export interface FichaCustosTipoObra {
  id: string;
  codigo: string;
  nome: string;
  dominante: boolean;
  escoposMacro: FichaCustosEscopoMacro[];
}

/** Custos e Financeiro lido do banco. Valores monetários ainda não existem (tela mostra 0,00). */
export interface FichaCustosFinanceiro {
  descricaoObra: string | null;
  centroCusto: string | null;
  tiposObra: FichaCustosTipoObra[];
}

export interface FichaFinanceiraDetalhe {
  id: string;
  obraId: string;
  criadoEm: string;
  solicitante: string;
  statusFel: string | null;
  obra: FichaObraResumo;
  informacoesEmbarcacao: FichaInformacoesEmbarcacao;
  custosFinanceiro: FichaCustosFinanceiro;
  clienteFinal: string | null;
  contrato: string | null;
  diretorProjeto: string | null;
  responsavelPreparacao: string | null;
  responsavelExecucao: string | null;
  coordenador: string | null;
  /** Formato completo definido na HU 2684: lido de forma defensiva. */
  dadosFicha: Record<string, unknown> | null;
}

/** Prévia da ficha a gerar (HU 2684): mesmos blocos da ficha, mais a data de elaboração. */
export interface FichaPrevia {
  /** ISO 8601. */
  elaboradoEm: string;
  obra: FichaObraResumo;
  informacoesEmbarcacao: FichaInformacoesEmbarcacao;
  custosFinanceiro: FichaCustosFinanceiro;
}

/** Corpo do `POST`: campos editáveis, opcionais (`null` quando vazios), até 255 caracteres. */
export interface CriarFichaFinanceiraPayload {
  clienteFinal: string | null;
  contrato: string | null;
  diretorProjeto: string | null;
  responsavelPreparacao: string | null;
  responsavelExecucao: string | null;
  coordenador: string | null;
}

/** Resposta do `201`. */
export interface FichaFinanceiraCriada {
  id: string;
  /** ISO 8601. */
  elaboradoEm: string;
  geradoPor: string;
}

/** Anexo da ficha (HU 2688): `GET .../anexos`. */
export interface FichaAnexo {
  id: string;
  /** Identificação do documento, informada na inclusão. */
  identificacao: string;
  nomeArquivo: string;
  /** Ex.: `PDF`, `XLSX`, `JPEG`. */
  formato: string;
  tamanhoBytes: number;
}

/** Entrada da inclusão: vira `multipart/form-data` com `identificacao` e `arquivo`. */
export interface IncluirFichaAnexoPayload {
  identificacao: string;
  arquivo: File;
}

/** Binário do anexo, pronto para `downloadFile`. */
export interface FichaAnexoArquivo {
  content: Uint8Array;
  fileName: string | null;
  mimeType: string | null;
}
